import { cn } from "@/lib/utils";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

export default function StatCard({ 
  title, 
  value, 
  unit = "",
  goal,
  goalLabel = "Goal",
  trend,
  trendValue,
  inverse = false, // For compliance rates where higher is better
  icon: Icon,
  className
}) {
  // Determine status based on value vs goal
  const getStatus = () => {
    if (goal === undefined || goal === null || value === undefined || value === null) return "neutral";
    
    if (inverse) {
      // Higher is better (compliance rates)
      if (value >= goal) return "good";
      if (value >= goal * 0.9) return "warning";
      return "danger";
    } else {
      // Lower is better (infection rates)
      if (value <= goal) return "good";
      if (value <= goal * 1.2) return "warning";
      return "danger";
    }
  };

  const status = getStatus();
  
  const statusStyles = {
    good: "border-l-emerald-500 bg-emerald-50/50",
    warning: "border-l-amber-500 bg-amber-50/50",
    danger: "border-l-red-500 bg-red-50/50",
    neutral: "border-l-slate-300 bg-white"
  };

  const valueStyles = {
    good: "text-emerald-700",
    warning: "text-amber-700",
    danger: "text-red-700",
    neutral: "text-slate-900"
  };

  return (
    <div 
      className={cn(
        "stat-card bg-white rounded-lg border border-slate-200 shadow-sm p-5 border-l-4",
        statusStyles[status],
        className
      )}
      data-testid={`stat-card-${title?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && (
            <div className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center",
              status === "good" ? "bg-emerald-100" :
              status === "warning" ? "bg-amber-100" :
              status === "danger" ? "bg-red-100" : "bg-slate-100"
            )}>
              <Icon className={cn(
                "w-4 h-4",
                status === "good" ? "text-emerald-600" :
                status === "warning" ? "text-amber-600" :
                status === "danger" ? "text-red-600" : "text-slate-600"
              )} />
            </div>
          )}
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
            {title}
          </span>
        </div>
        
        {trend && (
          <div className={cn(
            "flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full",
            trend === "up" ? (inverse ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700") :
            trend === "down" ? (inverse ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700") :
            "bg-slate-100 text-slate-600"
          )}>
            {trend === "up" ? <TrendingUp className="w-3 h-3" /> :
             trend === "down" ? <TrendingDown className="w-3 h-3" /> :
             <Minus className="w-3 h-3" />}
            {trendValue}
          </div>
        )}
      </div>

      <div className="flex items-baseline gap-2">
        <span className={cn(
          "text-3xl font-bold font-mono",
          valueStyles[status]
        )} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value ?? '-'}
        </span>
        {unit && <span className="text-sm text-slate-500">{unit}</span>}
      </div>

      {goal !== undefined && goal !== null && (
        <div className="mt-2 flex items-center gap-2 text-xs">
          <span className="text-slate-500">{goalLabel}:</span>
          <span className={cn(
            "font-medium font-mono",
            status === "good" ? "text-emerald-600" :
            status === "warning" ? "text-amber-600" :
            status === "danger" ? "text-red-600" : "text-slate-600"
          )}>
            {inverse ? "≥" : "≤"}{goal}{unit}
          </span>
        </div>
      )}
    </div>
  );
}
